// 首頁天氣卡 (W47.1) — rendered second, directly after the hero.
//
// ### Why `surface-ice` and not a pale tint
//
// The obvious reading of 「冰藍」 is `--tint-hero`'s `#E3F3FF`, and building the
// card on it would have been wrong twice over. `globals.css:106-118` records
// W43's decision: 淡色「**每屏恰好一張**」, and 「下面全是飽和色，**沒有任何一行是
// 淡色底**」. The hero already spends that one pale card, so a second pale card on
// the same screen is the thing W43 withdrew.
//
// `surface-ice` is a different colour and a different tier: `#8CC8EE` at
// **9.41:1**, the peak tier that `DiaryHero` already uses on `/diaries/:id`
// (`DESIGN.md §18.0`).
//
// ### W57.0 — this card is a real `AccentCard` now
//
// Until this round it painted `surface-ice` **by hand**, on a raw `<section>`.
// That made it a saturated block on the screen which the accent table could
// not see: `ACCENT_SLOTS` declared two accents for `/dashboard` (lime and
// aqua) while the DOM had three saturated blocks, and `scripts/w42-route-audit.mjs`
// — whose rule is 「the table declares N, the DOM has M; the declaration is the
// contract, not the render」 — was comparing a number that the hand-painted
// card was invisible to.
//
// So the surface moved up into `AccentCard` (the primitive that carries
// `data-accent-card`), the table gained the matching row, and `bare` below
// means 「the host is painting the surface, do not paint one here」 — the same
// contract `DailyAnniversaryCard`'s `bare` prop already had.
//
// ### Ink on an L1 surface
//
// `DESIGN.md §355-361` is the measured table and it is not symmetric: on
// `surface-ice`, `ink-1` is 9.41:1 and `ink-2` is **3.89:1 — under AA**. So this
// card uses `text-ink-1` and its alpha steps only. `text-ink-3` would have been
// fine (`globals.css:336` remaps it on `surface-ice`) but `text-ink-2` silently
// would not have been, which is the whole reason the rule is written down rather
// than left to taste.

import { conditionFor, pct, temp, weekdayZh } from "@/lib/weather/wmo";
import type { GeoReason } from "@/lib/weather/geolocation";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import type { DashboardWeather } from "@/lib/api/dashboard";
import { cn } from "@/lib/utils/cn";

export interface WeatherCardProps {
  weather: DashboardWeather | null;
  /**
   * Ask the browser for location again.
   *
   * The fallback badge is a **control**, not a label: someone who dismissed the
   * permission prompt has no other way back to their own weather, and a card that
   * says 「未授權」 while offering nothing to do about it is a dead end.
   *
   * W47.7: once a permission is denied outright the browser stops prompting, so
   * re-pressing can legitimately change nothing — but it must still **say so**.
   * The card used to show 「未授權 · 顯示香港」 before the press and the same
   * string after it, which made a refusal, a missing fix and a timeout
   * indistinguishable, and all three read as 「失敗」. The reason is carried in
   * and named.
   */
  onRequestLocation?: () => void;
  /** True while a second request is in flight, so the control can say so. */
  requestingLocation?: boolean;
  /**
   * W47.7 — what the browser last answered. `unasked` is the first paint, before
   * anybody was asked; the card has not failed at anything yet and keeps the
   * original wording for it.
   */
  locationReason?: GeoReason;
  /**
   * W57.0 — drop this card's own surface, because the host (`AccentCard`) is
   * painting the saturated `surface-ice` block.
   *
   * Same contract as `DailyAnniversaryCard`'s `bare`: fill, border and shadow
   * all go, and the host's padding is the padding.
   */
  bare?: boolean;
}

/** W47.7 — the wording for each outcome. Only `denied` names a cause. */
const REASON_COPY: Partial<Record<GeoReason, string>> = {
  denied: DASHBOARD_I18N.weather.reasonDenied,
  timeout: DASHBOARD_I18N.weather.reasonTimeout,
  unavailable: DASHBOARD_I18N.weather.reasonUnavailable,
  unsupported: DASHBOARD_I18N.weather.reasonUnsupported,
};

export function WeatherCard({
  weather,
  onRequestLocation,
  requestingLocation = false,
  locationReason = "unasked",
  bare = false,
}: WeatherCardProps) {
  // The card keeps its slot when the block is absent, and says so. Dropping it
  // would make the page change shape between two accent cards, which reads as
  // the layout moving rather than as one thing being unavailable.
  const rootClass = cn(
    bare ? "" : "surface-ice rounded-2xl p-s-5 shadow-hero",
  );

  if (!weather || !weather.available) {
    return (
      <section
        data-testid="weather-card"
        data-available="false"
        className={rootClass}
      >
        <p className="text-meta font-semibold text-ink-1">{DASHBOARD_I18N.weather.eyebrow}</p>
        <p className="mt-s-2 text-body-sm font-semibold text-ink-1">
          {DASHBOARD_I18N.weather.unavailable}
        </p>
        <Credit source="Open-Meteo.com" />
      </section>
    );
  }

  const current = conditionFor(weather.code);
  // The payload carries its own emoji; `conditionFor` is the fallback for a
  // cached block whose code predates a table change.
  const emoji = weather.emoji || current.emoji;
  const label = weather.label || current.label;

  /**
   * W47.7 — what the badge says. 「取得位置中…」 always wins while a request is
   * in flight; then the browser's own answer, and only then the original
   * 「未授權 · 顯示香港」 for the states where nobody has been asked yet.
   *
   * A granted position has no badge at all — the card shows the place name, which
   * is the answer to 「我在哪裡」 rather than a restatement of where the coordinates
   * came from.
   */
  const reasonCopy = REASON_COPY[locationReason];
  const badgeText = requestingLocation
    ? DASHBOARD_I18N.weather.locating
    : (reasonCopy ?? DASHBOARD_I18N.weather.fallbackBadge);

  return (
    <section
      data-testid="weather-card"
      data-available="true"
      data-fallback={weather.is_fallback ? "true" : undefined}
      data-location-reason={locationReason}
      className={rootClass}
    >
      <div className="flex items-start justify-between gap-s-3">
        <div className="min-w-0">
          <p className="text-meta font-semibold text-ink-1">{DASHBOARD_I18N.weather.eyebrow}</p>
          <p className="mt-s-1 text-body-sm font-bold text-ink-1">
            {weather.place_label}
            {weather.is_fallback ? (
              onRequestLocation ? (
                <button
                  type="button"
                  data-testid="weather-fallback-badge"
                  data-reason={locationReason}
                  onClick={onRequestLocation}
                  disabled={requestingLocation}
                  aria-busy={requestingLocation || undefined}
                  className="ml-s-2 inline-flex min-h-11 items-center rounded-pill border border-ink-1/25 px-s-3 align-middle text-micro font-bold text-ink-1 press"
                >
                  {badgeText}
                </button>
              ) : (
                <span
                  data-testid="weather-fallback-badge"
                  data-reason={locationReason}
                  className="ml-s-2 inline-block rounded-pill border border-ink-1/25 px-s-2 py-px align-middle text-micro font-bold text-ink-1"
                >
                  {badgeText}
                </span>
              )
            ) : null}
          </p>

          <div className="mt-s-2 flex items-baseline gap-s-3">
            <span
              data-testid="weather-temp"
              className="text-display-2 font-extrabold tabular-nums tracking-tight text-ink-1"
            >
              {temp(weather.temperature_c)}
            </span>
            <span className="text-body-sm font-semibold text-ink-1/78">
              {DASHBOARD_I18N.weather.feels.replace("{value}", temp(weather.apparent_c))}
              <br />
              {DASHBOARD_I18N.weather.humidity.replace("{value}", pct(weather.humidity_pct))}
            </span>
          </div>

          <p className="mt-s-2 text-body-sm text-ink-1/78">
            {DASHBOARD_I18N.weather.today}{" "}
            <b className="font-bold text-ink-1">
              {DASHBOARD_I18N.weather.todayRange
                .replace("{high}", temp(weather.today_high_c))
                .replace("{low}", temp(weather.today_low_c))}
            </b>{" "}
            ·{" "}
            {DASHBOARD_I18N.weather.rainChance.replace(
              "{value}",
              pct(weather.today_rain_probability),
            )}
          </p>
        </div>

        {/* Emoji, not a line icon: a WMO code is a data value in the same class
            as a diary mood, under the same `AGENTS.md §3 #19` exception
            (ADR-0133), and it gets the same font stack `MoodCard` uses. */}
        <span
          data-testid="weather-icon"
          aria-label={label}
          role="img"
          className="emoji-font shrink-0 text-[34px] leading-none"
        >
          {emoji}
        </span>
      </div>

      {weather.days.length > 0 ? (
        <>
          <div className="my-s-4 h-px bg-ink-1/12" aria-hidden />
          <ul data-testid="weather-days" className="grid grid-cols-3 gap-s-2">
            {weather.days.map((day) => (
              <li
                key={day.date}
                data-testid={`weather-day-${day.date}`}
                // `rounded-md` (12px) — **not** `rounded-inner`. `rounded-inner` was written
                // here because the *mockup* base stylesheet defines `--r-inner`;
                // the build's theme has no `inner` key, so Tailwind emitted
                // nothing and these three boxes shipped with `border-radius: 0px`
                // while everything around them was rounded. Measured in the live
                // app before the fix: `0px` here against the card's `18px`.
                // No tool saw it — not tsc, not vitest, not the screenshot
                // review — because the defect is an **absent** rule, and every
                // existing gate looks for present-but-banned forms.
                // `hard-rule-audit.mjs` #17b now resolves every `rounded-*`.
                className="rounded-md bg-paper/55 px-s-2 py-s-2 text-center"
              >
                <p className="text-meta font-bold text-ink-1/78">
                  {weekdayZh(day.date)}
                </p>
                {/* The day rows carry the label on the glyph, not as visible text.
                    The emoji alone is `aria-hidden` chrome, so without this a
                    screen-reader user got three dates, three numbers and **no
                    idea what the weather was** — the current-conditions glyph
                    above already does it, and `AGENTS.md §3 #14`'s reason for
                    keeping the mood labels applies unchanged here. */}
                <span
                  role="img"
                  aria-label={day.label || conditionFor(day.code).label}
                  className="emoji-font my-px block text-title-3 leading-none"
                >
                  {day.emoji || conditionFor(day.code).emoji}
                </span>
                <p className="text-body-sm font-bold tabular-nums text-ink-1">
                  {temp(day.high_c)}
                </p>
                <p className="text-micro tabular-nums text-ink-1/78">
                  {DASHBOARD_I18N.weather.dayRange
                    .replace("{low}", temp(day.low_c))
                    .replace("{probability}", pct(day.rain_probability))}
                </p>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <Credit source={weather.source} />
    </section>
  );
}

/**
 * CC BY 4.0 attribution. The data licence requires it, which makes it a licence
 * obligation rather than a styling choice — and therefore not something a visual
 * review can be trusted to notice.
 */
function Credit({ source }: { source: string }) {
  return (
    <p data-testid="weather-credit" className="mt-s-3 text-micro text-ink-1/68">
      {DASHBOARD_I18N.weather.credit.replace("{source}", source)}
    </p>
  );
}
